import React, { useCallback, useEffect, useState } from 'react';
import { helpWhy } from '../../help/why';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Paper, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import SearchableSelect from '../common/SearchableSelect';
import AccountAutocomplete from '../common/AccountAutocomplete';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { useToast } from '../../context/ToastContext';
import { AccountService } from '../../services/account.service';
import { EmployeeLoanService } from '../../services/employeeLoan.service';
import { Account } from '../../interfaces/accounting.interface';
import {
    DisbursementFileType, IDisbursementBatch, IEmployeeLoan, LoanMfsFormat,
} from '../../interfaces/employeeLoan.interface';
import { depositAccountOptions } from '../../utils/postingAccounts';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { usePermission } from '../../hooks/usePermission';
import { useLoanAccounting } from '../../hooks/useLoanAccounting';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';

const FILE_TYPES: { id: DisbursementFileType; label: string }[] = [
    { id: 'BANK_ADVICE', label: 'Bank advice (CSV)' },
    { id: 'MFS', label: 'Mobile banking / MFS (CSV)' },
];
const MFS_FORMATS: { id: LoanMfsFormat; label: string }[] = [
    { id: 'GENERIC', label: 'Generic' },
    { id: 'BKASH', label: 'bKash' },
    { id: 'NAGAD', label: 'Nagad' },
];

// W3-D: groups APPROVED loans into one bank-advice or MFS file, then pays the whole batch out at once
// (one journal per loan; the loans become ACTIVE and recovery starts at the scheduled month).
const LoanDisbursementBatches: React.FC = () => {
    const { showSuccess, showApiError, showWarning } = useToast();
    const canManage = usePermission(PERMISSIONS.PAYROLL_MANAGE);
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();
    // Accounting off: a batch is just the bank/MFS file plus the register, no pay-from account.
    const { accountingOn } = useLoanAccounting(institutionId);
    const [batches, setBatches] = useState<IDisbursementBatch[]>([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [approved, setApproved] = useState<IEmployeeLoan[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [reference, setReference] = useState('');
    const [fileType, setFileType] = useState<DisbursementFileType>('BANK_ADVICE');
    const [mfsFormat, setMfsFormat] = useState<LoanMfsFormat>('GENERIC');
    const [payFrom, setPayFrom] = useState('');
    const [picked, setPicked] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            setBatches(await EmployeeLoanService.listBatches(institutionId));
        } catch (e) {
            showApiError(e, 'Failed to load disbursement batches');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { load(); }, [load]);

    const openCreate = async () => {
        setReference('');
        setFileType('BANK_ADVICE');
        setMfsFormat('GENERIC');
        setPayFrom('');
        setPicked(new Set());
        setOpen(true);
        try {
            const [page, assets] = await Promise.all([
                EmployeeLoanService.list(institutionId, { status: 'APPROVED', size: 200 }),
                accountingOn ? AccountService.getAccountsByType('ASSET') : Promise.resolve([]),
            ]);
            setApproved(page.content.filter(l => !l.disbursementBatchId));
            setAccounts(Array.isArray(assets) ? depositAccountOptions(assets) : []);
        } catch (e) {
            showApiError(e, 'Failed to load approved loans');
        }
    };

    const toggle = (id: string) => setPicked(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const create = async () => {
        setSaving(true);
        try {
            await EmployeeLoanService.createBatch({
                institutionId,
                reference: reference.trim(),
                fileType,
                mfsFormat: fileType === 'MFS' ? mfsFormat : null,
                payFromAccountId: accountingOn ? payFrom : null,
                batchDate: toLocalIsoDate(),
                loanIds: Array.from(picked),
            });
            setOpen(false);
            showSuccess('Batch created');
            await load();
        } catch (e) {
            showApiError(e, 'Failed to create the batch');
        } finally {
            setSaving(false);
        }
    };

    const act = async (b: IDisbursementBatch, fn: () => Promise<unknown>, done: string, fail: string) => {
        setBusyId(b.id);
        try {
            await fn();
            if (done) showSuccess(done);
            await load();
        } catch (e) {
            showApiError(e, fail);
        } finally {
            setBusyId(null);
        }
    };

    const download = (b: IDisbursementBatch) => act(b, async () => {
        const excluded = await EmployeeLoanService.downloadBatchFile(b.id);
        if (excluded > 0) {
            showWarning(`${excluded} row(s) have missing or invalid bank/wallet details; see the warnings in the file.`);
        }
    }, '', 'Failed to generate the file');

    const columns: Column<IDisbursementBatch>[] = [
        { key: 'ref', header: 'Reference', priority: 1, helpKey: 'payroll.loanBatches.col.ref', render: b => b.reference },
        { key: 'date', header: 'Date', priority: 2, helpKey: 'payroll.loanBatches.col.date', render: b => formatDate(b.batchDate) },
        { key: 'type', header: 'File', priority: 2, helpKey: 'payroll.loanBatches.col.type', render: b => (b.fileType === 'MFS' ? `MFS ${b.mfsFormat ?? ''}` : 'Bank advice') },
        { key: 'total', header: 'Total', priority: 1, align: 'right', helpKey: 'payroll.loanBatches.col.total', render: b => formatMoney(b.totalAmount) },
        {
            key: 'status', header: 'Status', priority: 1,
            helpKey: 'payroll.loanBatches.col.status', render: b => <Chip size="small" color={b.status === 'DISBURSED' ? 'success' : b.status === 'CANCELLED' ? 'default' : 'info'} label={humanizeEnum(b.status)} />,
        },
        {
            key: 'actions', header: 'Actions', helpKey: 'payroll.loanBatches.col.actions', role: 'actions', hideInCard: true,
            render: b => (
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {b.status !== 'CANCELLED' && (
                        <Button data-help="payroll.loanBatches.action.download" size="small" variant="outlined" disabled={busyId === b.id} onClick={() => download(b)}>Download file</Button>
                    )}
                    {canManage && b.status === 'DRAFT' && (
                        <>
                            
                            <Button data-help="payroll.loanBatches.action.disburse" size="small" variant="contained" disabled={busyId === b.id}
                                onClick={() => act(b, () => EmployeeLoanService.disburseBatch(b.id, toLocalIsoDate()), accountingOn ? 'Batch disbursed and journals posted' : 'Batch disbursed', 'Failed to disburse the batch')}>
                                Mark disbursed
                            </Button>
                            
                            
                            <Button data-help="payroll.loanBatches.action.cancel" size="small" color="error" disabled={busyId === b.id}
                                onClick={() => act(b, () => EmployeeLoanService.cancelBatch(b.id), 'Batch cancelled', 'Failed to cancel the batch')}>
                                Cancel
                            </Button>
                            
                        </>
                    )}
                </Stack>
            ),
        },
    ];

    const total = approved.filter(l => picked.has(l.id)).reduce((s, l) => s + l.principal, 0);
    const needsPayFrom = accountingOn && payFrom === '';
    const valid = reference.trim() !== '' && !needsPayFrom && picked.size > 0;
    const createWhy = saving ? 'Saving is in progress.'
        : reference.trim() === '' ? 'Enter a reference first.'
        : needsPayFrom ? 'Choose the account to pay from first.'
        : picked.size === 0 ? 'Select at least one loan first.' : null;

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="payroll.loanBatches.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }} data-help-section="payroll.loanBatches.toolbar">
                <Typography variant="h5">Loan Disbursement Batches</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {isSystemAdmin && (
                        <SearchableSelect data-help="payroll.loanBatches.institution" label="Select Institution" options={institutions} value={institutionId}
                            onChange={id => setInstitutionId(id)} getOptionLabel={i => i.name} clearable={false} sx={{ minWidth: 220 }} />
                    )}
                    {canManage && <Button data-help="payroll.loanBatches.new" variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!institutionId}>New batch</Button>}
                </Box>
            </Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <ResponsiveTable data-help-section="payroll.loanBatches.table" pageKey="loan-disbursement-batches" columns={columns} rows={batches} getRowKey={b => b.id}
                    emptyMessage="No disbursement batches yet." />
            )}

            <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ 'data-help-page': 'payroll.loanBatches.form.page' } as any} maxWidth="sm" fullWidth>
                <DialogTitle>New disbursement batch</DialogTitle>
                <DialogContent data-help-section="payroll.loanBatches.form.section.details">
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField data-help="payroll.loanBatches.form.reference" label="Reference" value={reference} onChange={e => setReference(e.target.value)} required fullWidth
                            inputProps={{ maxLength: 100 }} />
                        <SearchableSelect data-help="payroll.loanBatches.form.fileType" label="File type" options={FILE_TYPES} value={fileType}
                            onChange={id => id && setFileType(id as DisbursementFileType)} getOptionLabel={t => t.label} clearable={false} />
                        {fileType === 'MFS' && (
                            <SearchableSelect data-help="payroll.loanBatches.form.mfsLayout" label="MFS layout" options={MFS_FORMATS} value={mfsFormat}
                                onChange={id => id && setMfsFormat(id as LoanMfsFormat)} getOptionLabel={t => t.label} clearable={false} />
                        )}
                        {accountingOn && <AccountAutocomplete data-help="payroll.loanBatches.form.payFrom" label="Pay from (cash or bank)" accounts={accounts} value={payFrom} onChange={setPayFrom} required />}
                        <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 260, overflow: 'auto' }} data-help="payroll.loanBatches.form.loans">
                            <Typography variant="subtitle2">Approved loans not yet in a batch</Typography>
                            {approved.length === 0 && <Typography variant="body2" color="text.secondary">None available.</Typography>}
                            {approved.map(l => (
                                <FormControlLabel key={l.id} sx={{ display: 'flex' }}
                                    control={<Checkbox checked={picked.has(l.id)} onChange={() => toggle(l.id)} />}
                                    label={`${l.employeeName ?? 'Employee'} - ${formatMoney(l.principal)}`} />
                            ))}
                        </Paper>
                        {picked.size > 0 && <Alert severity="info">{picked.size} loan(s), total {formatMoney(total)}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button data-help="payroll.loanBatches.form.cancel" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                    <Button data-help="payroll.loanBatches.form.create" {...helpWhy(createWhy ? { kind: 'status', message: createWhy } : null)} variant="contained" onClick={create} disabled={!valid || saving}>Create batch</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LoanDisbursementBatches;
